/**
 * A request that came back with a status the caller cannot work with.
 *
 * The status is kept on the error so callers can tell a missing permission (401/403 - the user or the
 * whole company is not set up for that endpoint) from a temporary backend problem, without parsing a
 * message text.
 */
export default class ApiError extends Error {
    public readonly url: string;
    public readonly status: number;

    constructor(url: string, status: number, message: string) {
        super(message);
        this.url = url;
        this.status = status;
        // Extending Error breaks the prototype chain once this is compiled down to ES5, which would make
        // every "instanceof ApiError" false. Restoring it explicitly is the documented workaround.
        Object.setPrototypeOf(this, ApiError.prototype);
        this.name = "ApiError";
    }

    /** The request was rejected because the token or the company lacks the permission for this endpoint. */
    public get isAuthorizationError(): boolean {
        return this.status === 401 || this.status === 403;
    }

    /** Type guard for the catch blocks, which only see "any". */
    public static isApiError(e: any): e is ApiError {
        return e instanceof ApiError;
    }
}
